import { PageSurface } from "./PageBody";
import type { QueryRowType } from "@rocicorp/zero";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { sharedMutators } from "../../shared/mutators";
import { personalUseBlocked } from "../../shared/personalUse";
import { queries } from "../../shared/queries";
import { isWebpageRoute } from "../../shared/stepConfig";
import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import { config } from "../environment";
import { useFollow3b } from "../hooks/useFollow3b";
import { useGetPresence } from "../hooks/useGetPresence";
import { useCurrentUserId } from "../hooks/useCurrentUserId";
import { usePushLiveRequest } from "../hooks/usePushLiveRequest";
import { userDisplayName } from "../lib/userDisplayName";
import { useUserGroupMemberships } from "../hooks/useUserGroupMemberships";
import { useHasAccess } from "../hooks/useHasAccess";
import { useIsGitSynced } from "../hooks/useIsGitSynced";
import { useInspectShortcut } from "../hooks/useInspectShortcut";
import { useOnboarding } from "../hooks/useOnboarding";
import { useChatpadShortcut } from "../hooks/useChatpadShortcut";
import { riseInAnimation } from "../hooks/useNavTransition";
import { useInvestigateStepTest } from "../hooks/useInvestigateStepTest";
import { useWorkflowChat } from "../hooks/useWorkflowChat";
import { useCommitFiles } from "../hooks/useCommitFiles";
import { useCursorChatHistory } from "../hooks/useCursorChatHistory";
import { useSelectedExecution } from "../hooks/useSelectedExecution";
import { useSetPresence } from "../hooks/useSetPresence";
import type { StepTestFixtureSetupRequest } from "../lib/pinnedExecutions";
import { branchRunChat } from "../lib/branchRunChat";
import { computeChangedStepDirs } from "../lib/changedStepDirs";
import { blockedBySelfMerge as isBlockedBySelfMerge } from "../lib/blockedBySelfMerge";
import { emitErrorToast } from "../lib/errorToast";
import type { StatusState } from "../lib/chatStatus";
import type { PushLiveBranch } from "../lib/executePushLive";
import { resolveFollowTarget } from "../lib/followTarget";
import { hasPushableChanges } from "../lib/hasPushableChanges";
import { clearStepFiles } from "../lib/lastOpenStepFile";
import {
  forgetWorkflowBranch,
  recallWorkflowBranch,
  rememberWorkflowBranch,
} from "../lib/lastWorkflowBranch";
import { layoutWorkflowSteps } from "../lib/layoutWorkflowSteps";
import { mentionConnectorCatalog } from "../hooks/useMentions";
import { spaceVolumePath } from "../lib/apiPaths";
import { type ParsedStep, parseCommitData } from "../lib/parseCommitData";
import { rankIsolatedStep } from "../lib/rankIsolatedStep";
import { resolveWorkflowFileHref } from "../lib/resolveWorkflowFileHref";
import { resolveStorageFileHref } from "../lib/resolveStorageFileHref";
import {
  type BashFileTarget,
  inputToFileTarget,
  parseBashFileTarget,
  type StreamingBashWrite,
  type StreamingFileEdit,
} from "../lib/parseStreamingBashWrite";
import { playPop } from "../lib/sounds";
import { previousSourcePage } from "../lib/sourcePage";
import { uploadToolCallOutput } from "../lib/toolCallOutputs";
import {
  useMakeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../lib/urls";
import { type DriveCommand, driveLiveStep } from "../lib/webpageDebugBridge";
import {
  approvePlan,
  askQuestion,
  bash,
  type ChatTool,
  connectChatToApp,
  connectStepToApp,
  connectToApp,
  copyStorageFileToStep,
  editFile,
  execute,
  getPageConsole,
  getRunExecutions,
  getStepLastExecution,
  getStepTest,
  inspectRenderedPage,
  interactWithPage,
  listVersions,
  pinStepTest,
  pushLive,
  readFile,
  readSkillFile,
  readStorageFile,
  readVersionChat,
  runSkillScript,
  runStep,
  runStepTests,
  screenshotPage,
  seedBranchStorage,
  unpinStepTest,
  skillTool,
  webFetch,
  webSearch,
  writeFile,
} from "../tools";
import { Backdrop } from "./Backdrop";
import { BranchSelector, type ChatBannerParts } from "./BranchSelector";

import { Chat } from "./Chat";
import { ChatContent } from "./Chat/ChatContent";
import { ChatMessageNavOverlay } from "./Chat/ChatMessageNav";
import type {
  ChatHandle,
  ChatProps,
  CodeSnippet,
  ElementSnippet,
} from "./Chat/chatTypes";
import {
  gettingStartedSuggestions,
  workflowSuggestions,
} from "./Chat/workflowSuggestions";
import type { CodeSelection } from "./CodeEditor";
import { ConnectClaimPrompt } from "./ConnectClaimPrompt";
import { DeletedOnBranchBanner } from "./DeletedOnBranchBanner";
import {
  DragIndicator,
  dragIndicatorHidden,
  dragIndicatorShown,
} from "./DragIndicator";
import {
  WorkflowNavigation,
  WorkflowNavigationTitle,
} from "./WorkflowNavigation";
import { HistoryBanner } from "./HistoryBanner";
import { Icon } from "./Icon";
import { ImportWorkflowModal } from "./ImportWorkflowModal";
import { MainScrollContainer } from "./MainScrollContainer";
import { requestBranch, useLiveChat } from "./LiveChat";
import { MaximizedStep } from "./MaximizedStep";
import { ChatpadToggleButton } from "./PersonalChats/ChatpadToggleButton";
import {
  ONBOARDING_PLACEHOLDER,
  OnboardingWelcome,
} from "./Onboarding/OnboardingWelcome";
import { Resizable, usePanelFraction } from "./Resizable";
import {
  SidePanelDismiss,
  sidePanelOverlayStyles,
  useSidePanelOverlay,
} from "./SidePanelOverlay";
import { PanelTransition } from "./PanelTransition";
import { resolveStepTab } from "./stepTab";
import { gridPadding, gridStepHeight, StepMap } from "./StepMap";
import {
  autoStepMapDensity,
  computeStepMapGeometry,
  specForSpacing,
  type SpacingLevel,
  type StepMapDensity,
} from "./StepMap/stepMapGeometry";
import { selectStepFiles } from "./stepFilesDiff";
import { WorkflowActionsMenu } from "./WorkflowActionsMenu";
import { FeatureButton } from "./WorkflowFeatureButton";
import { WorkflowOverview } from "./WorkflowOverview";

export type WorkflowData = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadWorkflow>>
>;
export type BranchData = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadBranchMetadata>>
>;
export type BranchSummary = WorkflowData["branches"][number];
export type CommitData = BranchData["commits"][number];
export type { CommitFileData } from "../hooks/useCommitFiles";

export type StepData = ParsedStep;

const emptyStepPresence = new Map<string, Set<string>>();

type Props = {
  spaceId: string;
  workflowId: string;
  step?: string;
};

const Layout = styled(PageSurface)`
  position: relative;
  display: flex;
  flex-direction: row;
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 16px;
  box-shadow: var(--elevation-low);
`;

const Vertical = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 0;
  min-width: 0;
  background: var(--surface-primary);
  overflow: hidden;
  box-sizing: border-box;

  ${sidePanelOverlayStyles}

  &[data-overlay="true"] {
    border-left: 0.5px solid var(--border-alpha);
  }
`;

const PanelToggle = styled("div")`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 4;
`;

const ChatContainer = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex-shrink: 0;
  container-type: inline-size;
  background: var(--surface-primary);
  border-right: 0.5px solid var(--border-alpha);

  &[data-panel-open="false"] {
    border-right: none;
  }
`;

const WorkflowChatContent = styled(ChatContent)`
  --workflow-chat-gutter: var(--chat-content-gutter);
  flex: 1;
  min-height: 0;
  padding-bottom: var(--chat-content-gutter);

  &[data-onboarding] {
    animation: ${riseInAnimation};
  }

  & [data-current-conversation="true"] {
    margin-right: 0;
    --chat-table-scroll-inset: 4px;
  }
  /* The 24px floor keeps text clear of the message nav. */
  & [data-current-conversation="true"]:not([data-exploratory="true"]) {
    padding-inline: calc(var(--chat-feed-inset) - var(--chat-content-gutter));
    transition: padding-inline var(--motion-base) var(--ease-out);
  }

  [data-panel-open="false"] & [data-conversation-banner] {
    /* Leave room for the floating toggle when the centered column reaches it. */
    right: max(
      -2px,
      calc(
        54px - var(--chat-content-gutter) -
          max(0px, (100cqw - var(--chat-content-max-width)) / 2)
      )
    );
  }

  [data-panel-open="false"] & [data-exploratory-banner] {
    margin-right: max(
      -2px,
      calc(40px - max(0px, (100cqw - var(--chat-content-max-width)) / 2))
    );
  }
`;

const ChatResizeHandle = styled("div")`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
  user-select: none;
  z-index: 1;

  & [data-drag-indicator] {
    ${dragIndicatorHidden("translateX(4px)")}
  }

  &:hover [data-drag-indicator] {
    ${dragIndicatorShown}
  }
`;

const FOLLOW_CODE_TOOLS = new Set(["editFile", "writeFile", "readFile"]);
const FOLLOW_OUTPUT_TOOLS = new Set([
  "runStep",
  "execute",
  "inspectRenderedPage",
  "getPageConsole",
  "interactWithPage",
  "screenshotPage",
]);

const MIN_CHAT_WIDTH = 340;
const MAX_CHAT_WIDTH = 780;
const CHAT_WIDTH_KEY = "workflowChatWidth";

type WorkflowSessionArgs = {
  focus: { spaceId: string; workflowId: string; step?: string } | null;
  chatRef: RefObject<ChatHandle | null>;
  standalone: boolean;
  onClosePanel: () => void;
  onExit: () => void;
  navigationHeaderSlot?: HTMLElement | null;
};

export function useWorkflowSession({
  focus,
  chatRef,
  standalone,
  onClosePanel,
  onExit,
  navigationHeaderSlot,
}: WorkflowSessionArgs) {
  const routeSpaceId = focus?.spaceId ?? "";
  const workflowId = focus?.workflowId ?? "";
  const step = focus?.step;
  const makeUrl = useMakeUrl();
  const z = useZero();
  const [focusedWorkflow, workflowQuery] = useQuery(
    queries.loadWorkflow({ workflowId }),
    { enabled: !!focus }
  );
  const workflow = focus ? focusedWorkflow : undefined;
  const spaceId = workflow?.spaceId ?? routeSpaceId;
  const {
    connectingClaimId,
    branchId: urlBranchId,
    commitId: urlCommitId,
    showRuns,
    stepTab,
    execution,
    file,
    tab,
    map,
    density,
    spacing,
    connectors,
    navigation,
  } = useCurrentUrl(routes.space.workflow);
  const [onWorkflowRoute] = useRouteMatch(routes.space.workflow);
  const [onPersonalWorkflowRoute] = useRouteMatch(routes.personal.workflow);
  const personalWorkflowParams = useCurrentUrl(routes.personal.workflow);

  useEffect(() => {
    if (spaceId === routeSpaceId) return;
    if (onWorkflowRoute) {
      navigate(null, { spaceId }, { replace: true });
    } else if (onPersonalWorkflowRoute) {
      navigate(
        routes.space.workflow,
        { ...personalWorkflowParams, spaceId, workflowId },
        { replace: true }
      );
    }
  }, [
    spaceId,
    routeSpaceId,
    workflowId,
    onWorkflowRoute,
    onPersonalWorkflowRoute,
    personalWorkflowParams,
  ]);

  useEffect(() => {
    if (!onWorkflowRoute || !urlBranchId) return;
    rememberWorkflowBranch(workflowId, urlBranchId);
  }, [onWorkflowRoute, urlBranchId, workflowId]);

  // Restoring only on arrival, and only for a URL that names no version at all,
  // leaves later navigation to a bare URL — pushing live, deleting the branch —
  // and shared links that pin a commit meaning what they say.
  const arrivedAtWorkflowRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      !standalone ||
      !onWorkflowRoute ||
      arrivedAtWorkflowRef.current === workflowId
    )
      return;
    arrivedAtWorkflowRef.current = workflowId;
    if (urlBranchId || urlCommitId) return;
    const lastBranchId = recallWorkflowBranch(workflowId);
    if (!lastBranchId) return;
    navigate(
      routes.space.workflow,
      { spaceId, workflowId, branchId: lastBranchId, commitId: "" },
      { replace: true }
    );
  }, [
    standalone,
    onWorkflowRoute,
    urlBranchId,
    urlCommitId,
    workflowId,
    spaceId,
  ]);

  const [allConnectorsRaw, connectorsResult] = useQuery(
    queries.connectorsIndex()
  );
  const [spaceGrants, grantsResult] = useQuery(
    queries.roleGrantsByScope({ scopeId: spaceId })
  );
  const rbacCanWrite = useHasAccess("space:write", spaceId);
  const canPushLive = useHasAccess("space:push_live", spaceId);
  const currentUserId = useCurrentUserId();
  const isGitSynced = useIsGitSynced(spaceId);
  const { memberships: userGroupMemberships, loaded: membershipsLoaded } =
    useUserGroupMemberships();
  const connectorsReady =
    membershipsLoaded &&
    [workflowQuery, connectorsResult, grantsResult].every(
      (result) => result.type === "complete"
    );
  const spaceMode = workflow?.space?.mode;
  const allConnectors = useMemo(
    () =>
      allConnectorsRaw.filter(
        (app) =>
          (spaceMode === undefined ||
            !personalUseBlocked(app, { kind: "space", mode: spaceMode })) &&
          isSupplierAvailableFromGrants(
            "space_uses_connected_apps",
            spaceGrants,
            app.roleGrantsAsScope,
            userGroupMemberships
          )
      ),
    [allConnectorsRaw, spaceGrants, userGroupMemberships, spaceMode]
  );
  const [focusedBranch, branchQuery] = useQuery(
    queries.loadBranchMetadata({
      workflowId,
      branchId: urlBranchId || undefined,
    }),
    { enabled: !!focus }
  );
  const branch = focus ? focusedBranch : undefined;
  const branchId = branch?.id;

  const isOnMainBranch = branch?.name === "main";
  const branchHeadCommitId = branch?.headCommitId ?? null;
  const [headCommitFiles, headFilesQuery] = useCommitFiles(
    branchHeadCommitId ?? ""
  );

  // Git sync tombstoned this workflow on this branch: it's read-only until
  // restored, since edits here would never sync back to the remote.
  const isDeletedOnBranch = (workflow?.branches ?? []).some(
    (b) => b.id === branch?.id && b.spaceBranchMembers.length > 0
  );
  const isViewingHistory = !!urlCommitId && urlCommitId !== branchHeadCommitId;
  const canWrite = rbacCanWrite && !isDeletedOnBranch && !isViewingHistory;
  const commit = isViewingHistory
    ? branch?.commits.find((c) => c.id === urlCommitId)
    : branch?.headCommit;

  const [branchCommits] = useQuery(
    queries.loadBranchChats({
      branchId: !isOnMainBranch ? (branch?.id ?? "") : "",
      entriesLimit: 0,
    })
  );
  const history = useCursorChatHistory(
    isOnMainBranch ? (commit?.id ?? "") : (branchId ?? ""),
    isOnMainBranch
      ? { type: "commit", commitId: commit?.id ?? "" }
      : {
          type: "workflow",
          chatIds: branchCommits.flatMap((c) => (c.chats ? [c.chats.id] : [])),
          branchId: branchId ?? "",
        },
    !!branchId && !!commit
  );
  const { onLoadOlder, loadingOlder } = history;

  const forkHistory = useCursorChatHistory(
    branch?.forkCommitId ?? "",
    { type: "commit", commitId: branch?.forkCommitId ?? "" },
    !isOnMainBranch && !!branch?.forkCommitId
  );
  const forkEntries = forkHistory.entries;

  const mainBranch = workflow?.branches.find((b) => b.name === "main");
  const liveCheckpoints = (mainBranch?.commits ?? [])
    .filter((c) => c.message)
    .toSorted((a, b) => a.createdAt - b.createdAt);
  const liveVersion = liveCheckpoints.findIndex((c) => c.id === commit?.id) + 1;
  const liveHistoryLabel =
    liveVersion > 0 ? `v${liveVersion} chat history` : "Chat history";
  const previousLiveVersion =
    liveCheckpoints.findIndex((c) => c.id === branch?.forkCommitId) + 1;
  const previousChatFromLive =
    mainBranch &&
    (mainBranch.headCommitId === branch?.forkCommitId ||
      mainBranch.commits.some((c) => c.id === branch?.forkCommitId));

  const headCommitChat = branch?.headCommitChat;
  const headCommitChatId = headCommitChat?.id ?? null;
  const runChat = branchRunChat(headCommitChat, branchCommits);
  const respondingAt = runChat?.respondingAt ?? null;

  const [isDeploying, setIsDeploying] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [pendingZip, setPendingZip] = useState<{
    workflowId: string;
    file: File;
    attach: () => void;
  } | null>(null);
  if (pendingZip && (!canWrite || pendingZip.workflowId !== workflowId)) {
    setPendingZip(null);
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const { width: containerWidth, height: containerHeight } = containerSize;
  const [hoveredStepCanonicalId, setHoveredStepCanonicalId] = useState<
    string | null
  >(null);
  const [monitorHoveredStepIds, setMonitorHoveredStepIds] =
    useState<Set<string> | null>(null);
  const [runsTabStepIds, setRunsTabStepIds] = useState<Set<string> | null>(
    null
  );
  const handleMonitorStepsHover = useCallback(
    (canonicalIds: ReadonlyArray<string> | null) => {
      setMonitorHoveredStepIds(canonicalIds ? new Set(canonicalIds) : null);
    },
    []
  );
  const handleRunsTabStepsChange = useCallback(
    (canonicalIds: ReadonlyArray<string> | null) => {
      setRunsTabStepIds(canonicalIds ? new Set(canonicalIds) : null);
    },
    []
  );

  const [historyCommitFiles, historyFilesQuery] = useCommitFiles(
    isViewingHistory ? urlCommitId : ""
  );
  const activeCommitFiles = useMemo(
    () => (isViewingHistory ? historyCommitFiles : headCommitFiles),
    [isViewingHistory, historyCommitFiles, headCommitFiles]
  );
  const commitFilesLoading = isViewingHistory
    ? historyFilesQuery.type !== "complete"
    : branchQuery.type !== "complete" ||
      (headCommitFiles.length === 0 && headFilesQuery.type !== "complete");

  const connectorsById = useMemo(
    () => new Map(allConnectors.map((ca) => [ca.id, ca])),
    [allConnectors]
  );

  const [appSlugRows] = useQuery(queries.workflowAppSlugs({ workflowId }));
  const slugToAppId = useMemo(
    () => new Map(appSlugRows.map((r) => [r.subKey, r.connectedAppId])),
    [appSlugRows]
  );

  const mentionConnectors = useMemo(() => {
    const seen = new Set<string>();
    return appSlugRows.flatMap((row) => {
      const app = connectorsById.get(row.connectedAppId);
      if (!app || seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [appSlugRows, connectorsById]);

  const mentionTenantConnectors = useMemo(() => {
    const seen = new Set<string>();
    return allConnectors.flatMap((app) => {
      if (seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [allConnectors]);

  const [activityWorkflow, stepActivityQuery] = useQuery(
    branch
      ? queries.loadStepActivity({ workflowId, branchId: branch.id })
      : undefined
  );
  const stepCounterMap = new Map(
    activityWorkflow?.stepActivity.map((row) => [
      row.stepDir,
      {
        executionCount: row.executionCount ?? 0,
        lastStatus: row.latestStatus ?? null,
        lastKind: row.latestKind ?? null,
        lastExecutionId: row.latestExecutionId ?? null,
        lastCompletedAt: row.latestCompletedAt ?? null,
      },
    ])
  );

  // Load the fork separately for change detection on non-main branches.
  const forkCommitId =
    !isOnMainBranch && branch?.forkCommitId ? branch.forkCommitId : "";
  const [forkCommitFiles, forkFilesQuery] = useCommitFiles(forkCommitId);

  // Detect which steps have changed on this branch compared to main.
  // Only applies to branches forked from main (have a forkCommitId).
  // Only computed once the branch query is complete to avoid a flash where
  // all steps appear changed while data is still streaming in.
  const changedCanonicalIds = useMemo(() => {
    if (isOnMainBranch || !branch || branchQuery.type !== "complete")
      return new Set<string>();
    if (!branch.forkCommitId) return new Set<string>();
    if (branch.forkCommitId === branch.headCommitId) return new Set<string>();
    if (
      forkFilesQuery.type !== "complete" ||
      headFilesQuery.type !== "complete"
    )
      return new Set<string>();
    return computeChangedStepDirs(forkCommitFiles, headCommitFiles);
  }, [
    isOnMainBranch,
    branch,
    branchQuery.type,
    forkFilesQuery.type,
    headFilesQuery.type,
    forkCommitFiles,
    headCommitFiles,
  ]);

  // Wait for both file queries to complete before deciding. headCommitId
  // updates instantly when a new commit lands, but its commit_files take
  // seconds to sync — comparing against an empty set during that gap shows
  // false-positive changes. forkCommitId may be null on a brand-new
  // workflow (main is created on first push), in which case we compare
  // against an empty fork side.
  const hasChangesToPush = useMemo(() => {
    if (isOnMainBranch || !branch || branchQuery.type !== "complete")
      return false;
    if (!branch.headCommitId) return false;
    if (branch.forkCommitId === branch.headCommitId) return false;
    if (respondingAt != null) return false;
    if (
      forkFilesQuery.type !== "complete" ||
      headFilesQuery.type !== "complete"
    )
      return false;
    return hasPushableChanges(forkCommitFiles, headCommitFiles);
  }, [
    isOnMainBranch,
    branch,
    branchQuery.type,
    respondingAt,
    forkFilesQuery.type,
    headFilesQuery.type,
    forkCommitFiles,
    headCommitFiles,
  ]);

  const { steps, links, workflowReadme } = parseCommitData(
    activeCommitFiles,
    connectorsById,
    stepCounterMap,
    slugToAppId
  );

  const mentionSteps = steps.map((s) => ({ name: s.name, color: s.color }));

  const layout = layoutWorkflowSteps(steps, links, {
    compareIsolated: (a, b) => rankIsolatedStep(a) - rankIsolatedStep(b),
  });

  const selectedStep = steps.find((s) => s.name === step);

  const [codeSnippets, setCodeSnippets] = useState<CodeSnippet[]>([]);
  const stepRef = useRef(selectedStep);
  useEffect(() => {
    stepRef.current = selectedStep;
  });
  const handleCodeSelectionChange = (selections: CodeSelection[]) => {
    if (selections.length === 0) return;
    const stepName = stepRef.current?.name ?? "";
    const color = stepRef.current?.color ?? "sky";
    const enriched = selections.map((s) => ({
      ...s,
      filePath: `${stepName}/${s.filePath}`,
      id: `${stepName}/${s.id}`,
      color,
    }));
    setCodeSnippets((prev) => {
      const seen = new Set(prev.map((s) => s.id));
      const added = enriched.filter((s) => !seen.has(s.id));
      return added.length ? [...prev, ...added] : prev;
    });
  };
  const removeCodeSnippet = useCallback((id: string) => {
    setCodeSnippets((prev) => prev.filter((s) => s.id !== id));
  }, []);
  const clearCodeSnippets = useCallback(() => setCodeSnippets([]), []);

  const [elementSnippets, setElementSnippets] = useState<ElementSnippet[]>([]);
  const [htmlInspectMode, setHtmlInspectMode] = useState(false);
  useInspectShortcut(() => setHtmlInspectMode((v) => !v));
  const selectedStepColor = selectedStep?.color ?? "sky";
  const selectedStepName = selectedStep?.name;
  if (
    elementSnippets.some(
      (s) => s.stepName === selectedStepName && s.color !== selectedStepColor
    )
  ) {
    setElementSnippets(
      elementSnippets.map((s) =>
        s.stepName === selectedStepName && s.color !== selectedStepColor
          ? { ...s, color: selectedStepColor }
          : s
      )
    );
  }
  const handleElementSelection = (
    element: Omit<ElementSnippet, "stepName" | "color">
  ) => {
    const stepName = stepRef.current?.name ?? "";
    const color = stepRef.current?.color ?? "sky";
    const enriched = { ...element, stepName, color };
    setElementSnippets((prev) => {
      if (
        element.cssPath &&
        prev.some(
          (s) => s.stepName === stepName && s.cssPath === element.cssPath
        )
      )
        return prev;
      return [...prev, enriched];
    });
  };
  const removeElementSnippet = useCallback((id: string) => {
    setElementSnippets((prev) => {
      const snippet = prev.find((s) => s.id === id);
      if (snippet) {
        window.dispatchEvent(
          new CustomEvent("3b-deselect-element", {
            detail: { index: snippet.index },
          })
        );
      }
      return prev.filter((s) => s.id !== id);
    });
  }, []);
  const deselectElementByIndex = useCallback((index: number) => {
    setElementSnippets((prev) => prev.filter((s) => s.index !== index));
  }, []);
  const clearElementSnippets = useCallback(() => setElementSnippets([]), []);

  const { latestId: latestExecutionId } = useSelectedExecution(
    selectedStep?.id ?? "",
    workflowId,
    branch?.id ?? ""
  );

  const soundsEnabled =
    !isViewingHistory &&
    Boolean(workflow) &&
    stepActivityQuery.type === "complete";

  useSetPresence(workflowId, branch?.id, selectedStep?.id);

  useEffect(() => {
    const mark = (sourcePage?: string) => {
      z.mutate(sharedMutators.markWorkflowViewed({ workflowId, sourcePage }));
    };
    if (!workflowId) return;
    mark(previousSourcePage(workflowId));
    return () => mark();
  }, [z, workflowId]);

  useEffect(() => () => clearStepFiles(), [workflowId]);

  const { presentByStep: presentByBranchAndStep, presentBranchesByUser } =
    useGetPresence();
  const presentByStep = branch
    ? (presentByBranchAndStep.get(branch.id) ?? emptyStepPresence)
    : emptyStepPresence;

  const branchStart = isOnMainBranch ? commit : branch?.commits[0];

  const entries = history.entries;
  // A draft started from this user’s private live chat shows that chat above its kickoff, to them alone.
  // Subscribed on live too, so a branch continued from there opens with it already loaded.
  const [ownLiveChat] = useQuery(queries.loadLiveChat({ workflowId }));
  const liveChatEntries = (ownLiveChat?.chat?.entries ?? []).toSorted(
    (a, b) => a.createdAt - b.createdAt
  );
  const requestedBranchId = (entry: (typeof liveChatEntries)[number]) =>
    (entry.toolCall?.output as { branchId?: string | null } | null)?.branchId;
  const branchRequestIndex = liveChatEntries.findLastIndex(
    (entry) => requestedBranchId(entry) === branch?.id
  );
  // What was asked before an earlier branch request belongs to that branch.
  const previousRequestIndex = liveChatEntries.findLastIndex(
    (entry, index) => index < branchRequestIndex && !!requestedBranchId(entry)
  );
  const liveOriginEntries = (isOnMainBranch || branchRequestIndex < 0
    ? []
    : liveChatEntries.slice(
        previousRequestIndex + 1,
        branchRequestIndex + 1
      )) as unknown as ChatProps["entries"];
  const respondingToolInput = respondingAt
    ? (entries.findLast((e) => e.entryType === "AssistantToolUse")?.toolCall
        ?.input as Record<string, string>)
    : undefined;
  const aiPresentOnStepName =
    respondingToolInput?.step ??
    respondingToolInput?.to ??
    respondingToolInput?.from;

  const [streamingAgentStep, setStreamingAgentStep] = useState<
    string | undefined
  >();
  const [streamingFileWrite, setStreamingFileWrite] =
    useState<StreamingBashWrite | null>(null);
  const [streamingFileEdit, setStreamingFileEdit] =
    useState<StreamingFileEdit | null>(null);
  const [streamingFileTarget, setStreamingFileTarget] =
    useState<BashFileTarget | null>(null);

  // Walk back through the current turn's tool calls and bail on the first tool
  // that names no file, so a newer runStep doesn't get shadowed by the previous
  // write's file target.
  const finalizedFileTarget = useMemo<BashFileTarget | null>(() => {
    if (!respondingAt) return null;
    const turnStart =
      entries.findLastIndex((e) => e.entryType === "UserMessage") + 1;
    for (let i = entries.length - 1; i >= turnStart; i--) {
      const entry = entries[i]!;
      if (entry.entryType !== "AssistantToolUse") continue;
      const input = entry.toolCall?.input as {
        command?: string;
        path?: string;
      } | null;
      const target = inputToFileTarget(input ?? undefined);
      if (target) return target;
      if (!input?.command) return null;
    }
    return null;
  }, [respondingAt, entries]);

  const stepNames = new Set(steps.map((s) => s.name));
  const stepNameList = steps.map((s) => s.name);
  const resolveChatFileHref = (href: string) => {
    const storageHref =
      branchId &&
      resolveStorageFileHref(href, (volumeName) =>
        spaceVolumePath(spaceId, branchId, volumeName)
      );
    return storageHref || resolveWorkflowFileHref(href, stepNameList, makeUrl);
  };
  const followTarget = resolveFollowTarget({
    streamingFileWrite,
    streamingFileTarget,
    finalizedFileTarget,
    streamingAgentStep,
    aiPresentOnStepName,
    stepNames,
  });

  const [preferences] = useQuery(queries.userPreferences());
  const follow3bByDefault = preferences?.follow3bByDefault === "on";
  const suggestFollow3b = preferences?.suggestFollow3b !== "off";

  const { isFollowing: isFollowing3b, setIsFollowing: setIsFollowing3b } =
    useFollow3b({
      agentStepName: followTarget.step,
      agentFilePath: followTarget.filePath,
      latestExecutionId,
      isResponding: respondingAt != null,
      autoFollow: follow3bByDefault,
      branchId,
    });

  const expandOutput = (() => {
    if (!respondingAt) return false;
    if (followTarget.filePath) return false;
    const turnStart =
      entries.findLastIndex((e) => e.entryType === "UserMessage") + 1;
    for (let i = entries.length - 1; i >= turnStart; i--) {
      const name = entries[i]!.toolCall?.toolName;
      if (!name) continue;
      if (FOLLOW_OUTPUT_TOOLS.has(name)) return true;
      if (FOLLOW_CODE_TOOLS.has(name)) return false;
      if (name === "bash") {
        const command = (entries[i]!.toolCall?.input as { command?: string })
          ?.command;
        if (command && parseBashFileTarget(command)) return false;
      }
    }
    return false;
  })();

  const [followStatus, setFollowStatus] = useState<StatusState>("resting");
  const [showFollowNudge, setShowFollowNudge] = useState(false);
  const followNudgeFiredForBranchRef = useRef<string | null>(null);
  const hasPriorAssistantEntry = entries.some(
    (e) =>
      e.entryType === "AssistantMessage" || e.entryType === "AssistantToolUse"
  );
  useEffect(() => {
    if (respondingAt == null) return;
    if (isFollowing3b) return;
    if (hasPriorAssistantEntry) return;
    if (follow3bByDefault) return;
    if (!suggestFollow3b) return;
    const branchKey = branchId ?? "main";
    if (followNudgeFiredForBranchRef.current === branchKey) return;
    const show = setTimeout(() => {
      followNudgeFiredForBranchRef.current = branchKey;
      setShowFollowNudge(true);
      playPop();
    }, 1000);
    return () => clearTimeout(show);
  }, [
    respondingAt,
    isFollowing3b,
    hasPriorAssistantEntry,
    branchId,
    follow3bByDefault,
    suggestFollow3b,
  ]);

  useEffect(() => {
    if (!showFollowNudge) return;
    const hide = setTimeout(() => setShowFollowNudge(false), 5000);
    return () => clearTimeout(hide);
  }, [showFollowNudge]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (
        e.defaultPrevented ||
        e.isComposing ||
        !e.altKey ||
        e.metaKey ||
        e.shiftKey ||
        e.ctrlKey ||
        e.code !== "KeyF" ||
        document.querySelector("dialog[open]") ||
        (e.target instanceof Element &&
          e.target.closest('[role="dialog"], [data-popover]'))
      )
        return;
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat) return;
      setIsFollowing3b((was) => !was);
      setShowFollowNudge(false);
    }
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [setIsFollowing3b]);

  const stepsTouchedThisTurn = useMemo(() => {
    if (!respondingAt) return new Set<string>();
    const turnStart =
      entries.findLastIndex((e) => e.entryType === "UserMessage") + 1;
    const names = new Set<string>();
    for (let i = turnStart; i < entries.length; i++) {
      const entry = entries[i]!;
      if (entry.entryType !== "AssistantToolUse") continue;
      const step = (entry.toolCall?.input as { step?: string } | null)?.step;
      if (step) names.add(step);
    }
    return names;
  }, [respondingAt, entries]);

  const { chatKey, onSend: handleChatSend } = useWorkflowChat(
    isOnMainBranch
      ? (branchStart?.id ?? branch?.id ?? "chat")
      : (branch?.id ?? "chat"),
    branchId ?? "",
    isOnMainBranch,
    commit?.id
  );

  const prevCheckpoint = useMemo(() => {
    if (!isOnMainBranch || !commit || !branch) return undefined;
    const commitsById = new Map(branch.commits.map((c) => [c.id, c]));
    let current = commit.parentCommitId
      ? commitsById.get(commit.parentCommitId)
      : undefined;
    while (current) {
      if (current.message) {
        const checkpoints = branch.commits.filter((c) => c.message);
        const idx = checkpoints.findIndex((c) => c.id === current!.id);
        return {
          link: makeUrl(routes.space.workflow, {
            spaceId,
            workflowId,
            branchId: branch.id,
            commitId: current.id,
          }),
          versionLabel: `v${idx + 1}`,
        };
      }
      current = current.parentCommitId
        ? commitsById.get(current.parentCommitId)
        : undefined;
    }
    return undefined;
  }, [isOnMainBranch, commit, branch, spaceId, workflowId, makeUrl]);

  const nextCheckpoint = useMemo(() => {
    if (!isOnMainBranch || !commit || !branch || !isViewingHistory)
      return undefined;
    const checkpoints = branch.commits.filter((c) => c.message);
    const currentIdx = checkpoints.findIndex((c) => c.id === commit.id);
    // If current commit isn't a checkpoint, find the first checkpoint after it
    const startIdx =
      currentIdx >= 0
        ? currentIdx + 1
        : checkpoints.findIndex((c) => c.createdAt > commit.createdAt);
    if (startIdx < 0 || startIdx >= checkpoints.length) return undefined;
    const next = checkpoints[startIdx]!;
    const isHead = next.id === branch.headCommitId;
    return {
      link: makeUrl(routes.space.workflow, {
        spaceId,
        workflowId,
        branchId: branch.id,
        commitId: isHead ? "" : next.id,
      }),
      versionLabel: `v${startIdx + 1}`,
    };
  }, [
    isOnMainBranch,
    commit,
    branch,
    isViewingHistory,
    spaceId,
    workflowId,
    makeUrl,
  ]);

  useEffect(() => {
    if (!focus) return;
    if (workflowQuery.type !== "complete") return;
    if (branchQuery.type !== "complete") return;
    if (workflow?.space && !branch && urlBranchId) {
      forgetWorkflowBranch(workflowId);
      navigate(
        routes.space.workflow,
        { spaceId, workflowId, branchId: "" },
        { replace: true }
      );
      return;
    }
    if (!workflow?.space || !branch) {
      onExit();
    }
  });

  const showContent = !!(workflow?.space && branch && commit);

  const observeContainer = (el: HTMLDivElement | null) => {
    containerRef.current = el;
    if (!el) return;
    const measure = () => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      if (!height) return;
      setContainerSize((prev) =>
        // Keep a shown panel at its last size while hidden, but mount a never-shown one so it is ready to slide in.
        (!width && prev.height) ||
        (prev.width === width && prev.height === height)
          ? prev
          : { width, height }
      );
    };
    let pendingTransition: Animation | undefined;
    const observer = new ResizeObserver(() => {
      // Re-rendering the session every frame of a layout slide drops frames, so settle once it ends while CSS widens the contents.
      const transition = document
        .getAnimations()
        .find(
          (animation) =>
            animation instanceof CSSTransition &&
            animation.transitionProperty === "width" &&
            animation.playState === "running"
        );
      if (!transition) return measure();
      if (transition === pendingTransition) return;
      pendingTransition = transition;
      transition.finished.then(measure, measure);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      containerRef.current = null;
    };
  };

  const branchIdForTools = branch?.id ?? "";
  const mainBranchId = useMemo(
    () => workflow?.branches.find((b) => b.name === "main")?.id,
    [workflow?.branches]
  );

  const pushLiveBranch: PushLiveBranch | undefined = useMemo(
    () =>
      branch ? { id: branch.id, name: branch.name, workflowId } : undefined,
    [branch, workflowId]
  );
  const blockedBySelfMerge = isBlockedBySelfMerge(
    workflow,
    branch,
    currentUserId
  );
  const pushLiveRequest = usePushLiveRequest(branch);
  const webpageDebugArgs = useMemo(
    () => ({
      drive: (stepName: string, command: DriveCommand) =>
        driveLiveStep(stepName, command, (target) => {
          navigate(routes.space.workflow, {
            spaceId,
            workflowId,
            step: target,
            execution: "",
          });
        }),
    }),
    [spaceId, workflowId]
  );
  const {
    investigateFailure: handleInvestigateFailure,
    investigateFailures: handleInvestigateFailures,
  } = useInvestigateStepTest(chatRef);

  const chatTools: readonly ChatTool[] = [
    askQuestion,
    requestBranch,
    approvePlan,
    pushLive({
      branch: pushLiveBranch,
      mainBranchId,
      spaceId,
      canPushLive,
      canWrite,
      blockedBySelfMerge:
        blockedBySelfMerge && !pushLiveRequest.request?.approvedByUserId,
      gitSynced: isGitSynced,
      onInvestigateFailures: handleInvestigateFailures,
    }),
    bash({
      getStepColor: (name) => steps.find((s) => s.name === name)?.color,
    }),
    connectStepToApp({ branchId: branchIdForTools, spaceId }),
    connectChatToApp({ branchId: branchIdForTools, spaceId }),
    connectToApp({ branchId: branchIdForTools, spaceId }),
    editFile({
      getStepColor: (name) => steps.find((s) => s.name === name)?.color,
    }),
    execute,
    getRunExecutions,
    getStepLastExecution,
    getStepTest,
    pinStepTest,
    runStepTests,
    unpinStepTest,
    inspectRenderedPage(webpageDebugArgs),
    getPageConsole(webpageDebugArgs),
    interactWithPage(webpageDebugArgs),
    screenshotPage(webpageDebugArgs),
    readFile({
      getStepColor: (name) => steps.find((s) => s.name === name)?.color,
    }),
    readStorageFile,
    listVersions,
    readVersionChat,
    runStep,
    seedBranchStorage,
    copyStorageFileToStep({
      getStepColor: (name) => steps.find((s) => s.name === name)?.color,
    }),
    skillTool,
    readSkillFile,
    runSkillScript,
    webFetch,
    webSearch,
    writeFile({
      getStepColor: (name) => steps.find((s) => s.name === name)?.color,
    }),
  ];

  const handleChatProvideToolResult = useCallback<
    ChatProps["onProvideToolResult"]
  >(
    async (entryId, output, isError, options) => {
      const entryChatId = entries.find((entry) => entry.id === entryId)?.chatId;
      if (
        !entryChatId ||
        !headCommitChatId ||
        entryChatId !== headCommitChatId ||
        !branchId ||
        isViewingHistory
      )
        return false;
      const showSaveError = () => {
        emitErrorToast({
          icon: "circle-off",
          message: "The tool result couldn’t be saved.",
          buttonLabel: "Dismiss",
        });
        return false;
      };
      try {
        const outputId = await uploadToolCallOutput({
          chatId: headCommitChatId,
          entryId,
          output,
        });
        const result = await z.mutate(
          sharedMutators.provideSpaceChatToolResult({
            chatId: headCommitChatId,
            entryId,
            outputId,
            isError,
            branchId,
            pauseAfter: options?.pauseAfter,
            chatModel: options?.chatModel,
          })
        ).server;
        if (result.type === "error") return showSaveError();
        return true;
      } catch {
        return showSaveError();
      }
    },
    [entries, headCommitChatId, branchId, isViewingHistory, z]
  );

  const liveChat = useLiveChat({
    workflowId,
    spaceId,
    liveBranchId: mainBranchId ?? undefined,
    branchId: branch?.id,
    liveHistoryLabel,
    liveReviewVersion: commit && { commitId: commit.id, workflowId },
    onBuild: handleChatSend,
    enabled: isOnMainBranch && !isViewingHistory && !isDeletedOnBranch,
    entries,
    onLoadOlder,
    loadingOlder,
  });
  const volumeUploads = !liveChat.props;

  if (!showContent) {
    return null;
  }
  const spaceSlug = workflow.space.slug;

  const handleAskWriteReadme = () => {
    chatRef.current?.enqueueOrSend({
      text: "Write a readme for this workflow.",
      actionKind: "readme",
    });
  };

  const handleAddTests = () => {
    chatRef.current?.enqueueOrSend({
      text: "Add step tests to this workflow.",
      actionKind: "add_tests",
    });
  };

  const handleInvestigateFailingSteps = (stepNames: readonly string[]) => {
    const quoted = stepNames.map((n) => `“${n}”`);
    const plural = stepNames.length > 1;
    const opener = plural
      ? `The following steps are failing — their most recent runs ended in errors: ${quoted.join(", ")}.`
      : `The ${quoted[0]} step is failing — its most recent run ended in an error.`;
    chatRef.current?.enqueueOrSend({
      text: `${opener} Investigate ${plural ? "each one" : "it"}: read the step’s most recent errored execution — its input, logs, and error output — along with the step’s code, to find the root cause.

Explain the root cause${plural ? " for each" : ""}, recommend the fix you think is right, then use askQuestion to offer applying it (append “(Recommended)” to the option you recommend):
- Fix the step${plural ? "s" : ""} — apply the change, then re-run to confirm the error is resolved.
- Leave it for now — if the failure is expected, transient, or you need my input first.

Don’t change any step files unless I choose to fix.`,
      actionKind: "investigate_step_status",
    });
  };

  const handleAskSetupStepTest = ({
    stepDir,
    executionId,
  }: StepTestFixtureSetupRequest) => {
    chatRef.current?.enqueueOrSend({
      text: `Set up and add a step test for the “${stepDir}” step using execution ${executionId}, choosing a short, descriptive name for it. Read the execution input, the step code, Dockerfile volume declarations, and existing step-test fixtures first. If the step writes to volumes, provide only the initial volume fixtures needed to exercise the behavior; author SQLite databases as sqlite-dump SQL text (CREATE TABLE and INSERT statements), and use base64 only for other binary files. If the step writes a store that isn’t SQLite-compatible, first add a tests/serialize script to the step (POSIX sh, invoked as sh tests/serialize <volume-name>) that prints a stable text representation of that volume’s entire state, so the test compares meaning rather than raw bytes. Add the step test, then always run runStepTests for the step to confirm the new test passes, and tell me the result.`,
      actionKind: "step_test_fixture_setup",
      selectedStep: stepDir,
    });
  };

  const handleChatInterrupt = () => {
    if (!runChat) return;
    z.mutate(
      sharedMutators.interruptChat({
        entryId: nanoid(12),
        chatId: runChat.id,
        branchId: branch.id,
      })
    );
  };

  const hasSteps = steps.length > 0;
  const isMapExpanded = map === "expanded" && hasSteps;
  const mapDensityOverride =
    density === "small" || density === "medium"
      ? density
      : density === "large"
        ? "normal"
        : null;
  // Spacing and connector style are the same whether the map is collapsed or
  // expanded, so a chosen (or default) look carries across the toggle instead of
  // silently flipping with the mode.
  const mapSpacingLevel: SpacingLevel =
    spacing === "compact" || spacing === "cosy" || spacing === "spacious"
      ? spacing
      : "compact";
  const mapCurvedConnectors = hasSteps && connectors === "loose";
  const heightCap = Math.min(
    containerHeight * 0.5,
    Math.max(0, containerHeight - 400 - gridPadding * 2)
  );
  // Size the pane to the density StepMap will actually render — the auto-fit
  // when the size control is on "Auto", or the pinned density's own geometry so
  // a Small/Medium map doesn't leave a band of empty canvas below it. Auto may
  // be dragged up to normal-density height, where StepMap grows the cards.
  const autoStepMapHeight = (paneHeight: number) =>
    computeStepMapGeometry(
      layout,
      links,
      specForSpacing(
        autoStepMapDensity(layout, links, {
          width: containerWidth,
          height: paneHeight,
        }),
        mapSpacingLevel
      ),
      undefined,
      paneHeight
    ).height;
  const fullStepMapHeight = (density: StepMapDensity) =>
    computeStepMapGeometry(
      layout,
      links,
      specForSpacing(density, mapSpacingLevel)
    ).height;
  const stepMapMinFloor = Math.max(gridStepHeight + gridPadding * 2, 60);
  const maxStepMapHeight = hasSteps
    ? fullStepMapHeight(mapDensityOverride ?? "normal")
    : 0;
  const minStepMapHeight = Math.min(
    stepMapMinFloor,
    mapDensityOverride ? maxStepMapHeight : fullStepMapHeight("small")
  );
  const fitToCapHeight =
    hasSteps && !mapDensityOverride
      ? autoStepMapHeight(heightCap)
      : maxStepMapHeight;
  const defaultStepMapHeight =
    containerHeight > 0 ? Math.min(fitToCapHeight, heightCap) : null;

  const panel = containerHeight > 0 && (
    <Resizable
      Handle={
        isMapExpanded || !hasSteps ? null : (
          <div
            style={{
              height: 24,
              marginBottom: -24,
              display: "flex",
              paddingTop: 4,
              justifyContent: "center",
            }}
          >
            <DragIndicator data-drag-indicator />
          </div>
        )
      }
      minSize={minStepMapHeight}
      maxSize={maxStepMapHeight}
      minOppositeSize={400}
      defaultSize={defaultStepMapHeight ?? maxStepMapHeight}
      onDragStart={
        mapDensityOverride
          ? () => navigate(null, { density: "" }, { replace: true })
          : undefined
      }
      overrideSize={!hasSteps ? 0 : isMapExpanded ? containerHeight : undefined}
      snapUndraggedResize
      storageKey={`stepMapHeight:${workflowId}`}
      orientation="vertical"
      containerRef={containerRef}
      first={(() => {
        const topContent = commitFilesLoading ? null : selectedStep ? (
          <MaximizedStep
            onClosePanel={onClosePanel}
            allConnectors={allConnectors}
            connectorsReady={connectorsReady}
            key={selectedStep.name}
            step={selectedStep}
            allSteps={steps}
            spaceSlug={spaceSlug}
            customHostname={workflow.space?.customHostname}
            spaceId={spaceId}
            branch={branch}
            commitId={commit.id}
            headCommitFiles={headCommitFiles}
            files={selectStepFiles(activeCommitFiles, selectedStep.name)}
            baseFiles={
              branch.forkCommitId && branch.forkCommitId !== commit.id
                ? selectStepFiles(forkCommitFiles, selectedStep.name)
                : undefined
            }
            isReadOnly={isViewingHistory || isDeletedOnBranch}
            canWrite={canWrite}
            onInvestigateFailure={handleInvestigateFailure}
            onAskSetupStepTest={handleAskSetupStepTest}
            workflowPausedAt={workflow.pausedAt ?? null}
            onSelectionChange={handleCodeSelectionChange}
            codeSnippets={codeSnippets}
            onElementSelect={handleElementSelection}
            onElementDeselect={deselectElementByIndex}
            htmlInspectMode={htmlInspectMode}
            onHtmlInspectModeChange={setHtmlInspectMode}
            elementSnippets={elementSnippets.filter(
              (s) => s.stepName === selectedStep.name
            )}
            isBeingWritten={stepsTouchedThisTurn.has(selectedStep.name)}
            forceShowCode={isFollowing3b}
            onStopFollow={() => setIsFollowing3b(false)}
            expandOutput={isFollowing3b && expandOutput}
            followActive={isFollowing3b && followStatus === "building"}
            streamingFileWrite={
              streamingFileWrite?.stepDir === selectedStep.name
                ? streamingFileWrite
                : null
            }
            streamingFileEdit={
              streamingFileEdit?.stepDir === selectedStep.name
                ? streamingFileEdit
                : null
            }
          />
        ) : (
          <WorkflowOverview
            onClosePanel={onClosePanel}
            key={branch.id}
            workflowId={workflowId}
            branchId={branch.id}
            spaceSlug={spaceSlug}
            customHostname={workflow.space?.customHostname}
            spaceId={spaceId}
            branch={branch}
            headCommitFiles={headCommitFiles}
            canWrite={canWrite}
            isReadOnly={isViewingHistory || isDeletedOnBranch}
            readme={workflowReadme}
            steps={steps}
            allConnectors={allConnectors}
            connectorsReady={connectorsReady}
            inspectMode={htmlInspectMode}
            onInspectModeChange={setHtmlInspectMode}
            onElementSelect={handleElementSelection}
            onElementDeselect={deselectElementByIndex}
            elementSnippets={elementSnippets}
            hoveredCanonicalId={hoveredStepCanonicalId}
            onStepHover={setHoveredStepCanonicalId}
            onStepsHover={handleMonitorStepsHover}
            onRunsTabStepsChange={handleRunsTabStepsChange}
            onAskWriteReadme={handleAskWriteReadme}
            onAddTests={handleAddTests}
            onInvestigateFailingSteps={handleInvestigateFailingSteps}
          />
        );

        return (
          <div
            inert={isMapExpanded}
            aria-hidden={isMapExpanded || undefined}
            style={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              // With no steps there's no map pane below to carry the
              // bottom inset, so the overview keeps it itself.
              paddingBottom: hasSteps ? 0 : 12,
              opacity: isMapExpanded ? 0 : 1,
              transform: isMapExpanded ? "translateY(-24px)" : "translateY(0)",
              transition:
                "opacity var(--motion-base) var(--ease-out), transform var(--motion-base) var(--ease-out)",
            }}
          >
            <PanelTransition
              viewKey={
                selectedStep
                  ? `${selectedStep.canonicalId}:${resolveStepTab({
                      stepTab,
                      showRuns,
                      file,
                      execution,
                      browserApplicable: isWebpageRoute(selectedStep.routeType),
                      forceShowCode: isFollowing3b,
                      expandOutput,
                    })}`
                  : `workflow:${tab ?? ""}`
              }
              depth={selectedStep ? 1 : 0}
            >
              {topContent}
            </PanelTransition>
          </div>
        );
      })()}
      second={
        hasSteps ? (
          <StepMap
            key={branch.id}
            steps={steps}
            links={links}
            workflow={workflow}
            spaceSlug={spaceSlug}
            layout={layout}
            selectedStep={step ?? null}
            branchId={branch.id}
            commitId={commit.id}
            isOnMainBranch={isOnMainBranch}
            isExpanded={isMapExpanded}
            densityOverride={mapDensityOverride}
            spacingLevel={mapSpacingLevel}
            curvedConnectors={mapCurvedConnectors}
            dataSettled={!commitFilesLoading}
            isReadOnly={isViewingHistory || isDeletedOnBranch}
            canWrite={canWrite}
            aiPresentOnStepName={aiPresentOnStepName}
            presentByStep={presentByStep}
            enableSounds={soundsEnabled}
            changedCanonicalIds={changedCanonicalIds}
            highlightedCanonicalIds={
              monitorHoveredStepIds ??
              (!selectedStep && tab === "runs" ? runsTabStepIds : null)
            }
            hoveredCanonicalId={hoveredStepCanonicalId}
            onStepHover={setHoveredStepCanonicalId}
          />
        ) : null
      }
    />
  );

  const chatProps: ChatProps = {
    modelScope: { type: "branch", branchId: branch.id },
    onZipFile: canWrite
      ? (file, attach) => setPendingZip({ workflowId, file, attach })
      : undefined,
    acceptAnyFile: volumeUploads,
    entries: liveOriginEntries.length
      ? [...liveOriginEntries, ...entries]
      : entries,
    privateChatIds:
      liveOriginEntries.length && ownLiveChat?.chat
        ? [ownLiveChat.chat.id]
        : undefined,
    onLoadOlder,
    loadingOlder,
    reviewVersion: {
      commitId: commit.id,
      workflowId,
      draft: !isOnMainBranch,
      pushRequest:
        !isViewingHistory &&
        pushLiveRequest.request?.requestedByUserId === currentUserId
          ? {
              approvedBy: pushLiveRequest.request.approvedByUser
                ? userDisplayName(pushLiveRequest.request.approvedByUser)
                : null,
            }
          : undefined,
    },
    liveConversation:
      isOnMainBranch && !isViewingHistory && commit
        ? {
            id: commit.id,
            onLoadOlder,
            loadingOlder,
            label: liveHistoryLabel,
            reviewVersion: { commitId: commit.id, workflowId },
          }
        : undefined,
    previousConversation:
      !isOnMainBranch && branch.forkCommitId && forkEntries.length
        ? {
            // Shares the live pane’s identity, so continuing from live keeps the same history in place.
            id:
              previousChatFromLive && mainBranchId
                ? mainBranchId
                : branch.forkCommitId,
            label:
              previousLiveVersion > 0
                ? `v${previousLiveVersion} chat history`
                : previousChatFromLive
                  ? "Chat history"
                  : "Previous branch chat",
            entries: forkEntries,
            onLoadOlder: forkHistory.onLoadOlder,
            loadingOlder: forkHistory.loadingOlder,
            reviewVersion: previousChatFromLive
              ? { commitId: branch.forkCommitId, workflowId }
              : undefined,
          }
        : undefined,
    writeChatId: headCommitChatId,
    streamChatId: runChat?.id,
    draftKey: urlBranchId || "main",
    onSend: handleChatSend,
    onInterrupt: handleChatInterrupt,
    onProvideToolResult: handleChatProvideToolResult,
    prevCheckpointLink: prevCheckpoint?.link,
    prevCheckpointLabel: prevCheckpoint?.versionLabel,
    nextCheckpointLink: nextCheckpoint?.link,
    nextCheckpointLabel: nextCheckpoint?.versionLabel,
    isCurrentUserChat: !isViewingHistory && !isDeletedOnBranch,
    canWrite,
    tools: chatTools,
    spaceId,
    suggestions:
      entries.length > 0 || hasSteps
        ? workflowSuggestions
        : gettingStartedSuggestions,
    soundsEnabled,
    respondingAt,
    lastLoopHeartbeatAt: runChat?.lastLoopHeartbeatAt,
    awaitingAction: headCommitChat?.awaitingAction ?? false,
    planModeDefaultSurface: isOnMainBranch ? "branch" : "none",
    codeSnippets,
    removeCodeSnippet,
    clearCodeSnippets,
    elementSnippets,
    removeElementSnippet,
    clearElementSnippets,
    selectedStepName: selectedStep?.name,
    mentionSteps,
    mentionConnectors,
    mentionTenantConnectors,
    mentionConnectorCatalog,
    mentionConnectorContextLabel: "On this workflow",
    resolveFileHref: resolveChatFileHref,
    historyBanner: isViewingHistory ? (
      <HistoryBanner branch={branch} commit={commit} />
    ) : isDeletedOnBranch ? (
      <DeletedOnBranchBanner branch={branch} />
    ) : undefined,
    pendingPromptOverride: connectingClaimId ? (
      <ConnectClaimPrompt
        claimId={connectingClaimId}
        onClose={() =>
          navigate(null, { connectingClaimId: "" }, { replace: true })
        }
      />
    ) : undefined,
    onStatusClick: () => setIsFollowing3b((was) => !was),
    followActive: isFollowing3b,
    showFollowNudge,
    onDismissFollowNudge: () => setShowFollowNudge(false),
    onStreamingToolStep: setStreamingAgentStep,
    onStatusChange: setFollowStatus,
    onStreamingFileWrite: setStreamingFileWrite,
    onStreamingFileEdit: setStreamingFileEdit,
    onStreamingFileTarget: setStreamingFileTarget,
    ...liveChat.props,
  };

  const backHref =
    !standalone &&
    (onPersonalWorkflowRoute
      ? personalWorkflowParams.navigation
      : navigation) === "workflow"
      ? makeUrl(null, { navigation: "" })
      : makeUrl(routes.space, { spaceId });

  return {
    workflow,
    spaceId,
    branch,
    isOnMainBranch,
    isMapExpanded,
    hasPanelActivity:
      hasSteps || !!followTarget.step || !!followTarget.filePath,
    title: `${workflow.name} • ${config.title}`,
    chatKey: liveChat.key ?? chatKey,
    chatProps,
    zipModal: pendingZip && canWrite && (
      <ImportWorkflowModal
        file={pendingZip.file}
        defaultSpaceId={spaceId}
        integrationSpaceId={spaceId}
        onIntegrate={pendingZip.attach}
        onClose={() => setPendingZip(null)}
      />
    ),
    observeContainer,
    panel,
    frame: (
      render: (
        navigation: ReactNode,
        banner: ReactNode,
        chatBanner: ChatBannerParts
      ) => ReactNode
    ) => (
      <BranchSelector
        chatBanner={!standalone}
        workflow={workflow}
        currentBranch={branch}
        branchSummaries={workflow.branches}
        commitId={commit.id}
        spaceId={spaceId}
        presentBranchesByUser={presentBranchesByUser}
        onInvestigateFailures={handleInvestigateFailures}
        hasChangesToPush={hasChangesToPush}
        canWrite={canWrite}
        canCreateBranch={rbacCanWrite && !isDeletedOnBranch}
        canPushLive={canPushLive && !isViewingHistory}
        blockedBySelfMerge={blockedBySelfMerge}
        pushLiveRequest={pushLiveRequest}
        isDeletedOnBranch={isDeletedOnBranch}
        isDeploying={isDeploying}
        isMerging={isMerging}
        onDeploying={setIsDeploying}
        onMerging={setIsMerging}
      >
        {(branchNavigation, branchBanner, chatBanner) =>
          render(
            <WorkflowNavigation
              workflow={workflow}
              isReadOnly={isViewingHistory || isDeletedOnBranch}
              backHref={backHref}
              headerSlot={navigationHeaderSlot}
              menu={
                <WorkflowActionsMenu
                  isReadOnly={isViewingHistory}
                  workflowId={workflowId}
                  workflowName={workflow.name}
                  spaceId={spaceId}
                  hasLiveVersion={mainBranchId != null}
                  workflowLinkHidden={workflow.workflowLinkHidden ?? false}
                  workflowLinkCorner={workflow.workflowLinkCorner ?? null}
                  opener={<WorkflowNavigationTitle workflow={workflow} />}
                  afterDelete={onExit}
                />
              }
              optionsMenu={(target) => (
                <WorkflowActionsMenu
                  isReadOnly={isViewingHistory}
                  workflowId={workflowId}
                  workflowName={workflow.name}
                  spaceId={spaceId}
                  hasLiveVersion={mainBranchId != null}
                  workflowLinkHidden={workflow.workflowLinkHidden ?? false}
                  workflowLinkCorner={workflow.workflowLinkCorner ?? null}
                  target={target}
                  opener={
                    <FeatureButton
                      data-no-pill
                      aria-label={`Workflow options for ${workflow.name}`}
                      aria-haspopup="menu"
                    >
                      <Icon name="more" size={16} />
                    </FeatureButton>
                  }
                  afterDelete={onExit}
                />
              )}
            >
              {branchNavigation}
            </WorkflowNavigation>,
            branchBanner,
            chatBanner
          )
        }
      </BranchSelector>
    ),
  };
}

export function Workflow({ spaceId, workflowId, step }: Props) {
  const [panelOpen, setPanelOpen] = useState(true);
  const togglePanel = () => setPanelOpen((open) => !open);
  const closePanel = () => setPanelOpen(false);
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);
  const chatRef = useRef<ChatHandle>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [messageNavContainer, setMessageNavContainer] =
    useState<HTMLDivElement | null>(null);
  const [chatDragging, setChatDragging] = useState(false);
  const [chatWidthFraction, setChatWidthFraction] = usePanelFraction(
    CHAT_WIDTH_KEY,
    chatDragging
  );
  // A new user’s side panel waits for 3B to start building, after the sidebar has arrived.
  const onboardingState = useOnboarding(workflowId);
  const { onboarding, awaitingBuild } = onboardingState;

  const session = useWorkflowSession({
    focus: { spaceId, workflowId, step },
    chatRef,
    standalone: true,
    onClosePanel: togglePanel,
    onExit: () => navigate(routes.space, { spaceId }, { replace: true }),
    navigationHeaderSlot: headerSlot,
  });
  // Just after a switch the session can still describe the previous workflow, so only the onboarding workflow’s own build counts.
  if (
    awaitingBuild &&
    !onboarding &&
    session?.workflow.id === workflowId &&
    session.hasPanelActivity
  )
    onboardingState.revealPanel();
  const panelShown = panelOpen && !awaitingBuild;
  const showOrTogglePanel = () =>
    awaitingBuild ? onboardingState.revealPanel() : togglePanel();
  useChatpadShortcut(showOrTogglePanel, !onboarding);
  // Chat panel horizontal resize.
  const {
    isOverlay,
    workspaceRef: layoutRef,
    panelRef,
    observeWorkspace,
  } = useSidePanelOverlay(panelShown, closePanel);

  const onChatResizeHandleDown = (e: React.MouseEvent) => {
    e.preventDefault();
    let dragging = true;
    setChatDragging(true);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev: MouseEvent) => {
      if (!dragging) return;
      const layout = layoutRef.current;
      if (!layout) return;
      const rect = layout.getBoundingClientRect();
      const style = getComputedStyle(layout);
      const paddingLeft = parseFloat(style.paddingLeft);
      const innerWidth =
        rect.width - paddingLeft - parseFloat(style.paddingRight);
      const newWidth = ev.clientX - rect.left - paddingLeft;
      const clamped = Math.max(
        MIN_CHAT_WIDTH,
        Math.min(newWidth, MAX_CHAT_WIDTH, innerWidth - 360)
      );
      setChatWidthFraction(clamped / innerWidth);
    };
    const onUp = () => {
      dragging = false;
      setChatDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const handleChatResizeDoubleClick = () => {
    setChatWidthFraction(undefined);
  };
  // Keep the component identity stable so typing doesn’t remount the welcome and replay it.
  const OnboardingEmptyState = useCallback(
    ({ visible }: { visible: boolean }) => (
      <OnboardingWelcome
        workflowId={workflowId}
        ideasVisible={visible}
        onPick={(prompt) => chatRef.current?.enqueueOrSend({ text: prompt })}
      />
    ),
    [workflowId]
  );
  if (!session) return null;

  return (
    <>
      <title>{session.title}</title>
      {session.frame((navigation, branchBanner) => (
        <Backdrop
          permanentNavigation
          navigationHidden={onboarding}
          headerContent={
            <div ref={setHeaderSlot} style={{ display: "contents" }} />
          }
          navigation={navigation}
        >
          <MainScrollContainer
            ready
            resetOnNavigate={false}
            style={{ overflow: "visible" }}
          >
            <Layout ref={observeWorkspace}>
              <ChatContainer
                ref={chatContainerRef}
                data-panel-open={panelShown && !isOverlay}
                style={{
                  width:
                    !panelShown || isOverlay
                      ? "100%"
                      : `clamp(${MIN_CHAT_WIDTH}px, ${chatWidthFraction !== undefined ? `${chatWidthFraction * 100}%` : "50%"}, min(${MAX_CHAT_WIDTH}px, 100% - 360px))`,
                  ...(!chatDragging && {
                    transition: "width var(--motion-base) var(--ease-out)",
                  }),
                }}
              >
                <WorkflowChatContent data-onboarding={onboarding || undefined}>
                  <Chat
                    ref={chatRef}
                    key={session.chatKey}
                    {...session.chatProps}
                    {...(onboarding && {
                      onSend: (payload, onError) => {
                        onboardingState.finishWelcome();
                        session.chatProps.onSend(payload, onError);
                      },
                      EmptyState: OnboardingEmptyState,
                      placeholder: ONBOARDING_PLACEHOLDER,
                      placeholderHints: false,
                    })}
                    conversationBanner={onboarding ? undefined : branchBanner}
                    messageNavPortal={{
                      container: messageNavContainer,
                      wrapperRef: chatContainerRef,
                    }}
                  />
                  {session.zipModal}
                </WorkflowChatContent>
                <ChatMessageNavOverlay ref={setMessageNavContainer} />
              </ChatContainer>

              <Vertical
                ref={(el) => {
                  panelRef.current = el;
                  const cleanup = session.observeContainer(el);
                  return () => {
                    panelRef.current = null;
                    cleanup?.();
                  };
                }}
                role="region"
                aria-label="Side panel"
                tabIndex={panelShown && isOverlay ? -1 : undefined}
                aria-hidden={!panelShown || undefined}
                inert={!panelShown}
                data-overlay={isOverlay}
                data-expanded={panelShown}
              >
                {!isOverlay && (
                  <ChatResizeHandle
                    onMouseDown={onChatResizeHandleDown}
                    onDoubleClick={handleChatResizeDoubleClick}
                  >
                    <DragIndicator
                      data-drag-indicator
                      data-orientation="horizontal"
                    />
                  </ChatResizeHandle>
                )}
                {session.panel}
              </Vertical>
              {panelShown && isOverlay && (
                <SidePanelDismiss
                  aria-label="Dismiss panel"
                  tabIndex={-1}
                  data-no-pill=""
                  onPress={closePanel}
                  children={null}
                />
              )}
              {!onboarding && (
                <PanelToggle>
                  <ChatpadToggleButton
                    floating={!panelShown || !session.isMapExpanded}
                    isOpen={panelShown}
                    onToggle={showOrTogglePanel}
                  />
                </PanelToggle>
              )}
            </Layout>
          </MainScrollContainer>
        </Backdrop>
      ))}
    </>
  );
}
